import React, {useContext, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {AppointmentStatus} from '@common/models'

import {Container} from '../general/container/Container'
import {Typography} from '../general/typography/Typography'
import {Button} from '../general/button/Button'
import {SideModal} from '../general/modal/SideModal'
import {useSetAppointmentStatus} from '../../api/appointments'
import {
  NotificationContext,
  NotificationType,
} from '../notifications/NotificationContext'
import {TextArea} from '../general/textArea/TextArea'
import {InfoCard} from '../general/infoCard/InfoCard'

interface DNAAppointmentModalProps {
  isVisible: boolean
  onClose: () => void
  appointmentId: string
  facilityId: string
  originalDate: string
  patientName: string
  patientDob: string | null
  existingNote?: string
  showWarning: boolean
}

export const DNAAppointmentModal: React.FC<DNAAppointmentModalProps> = ({
  isVisible,
  onClose,
  appointmentId,
  facilityId,
  originalDate,
  patientName,
  patientDob,
  existingNote,
  showWarning,
}) => {
  const {t} = useTranslation<string>()
  const [note, setNote] = useState<string | null>(existingNote || null)
  const {addNotification} = useContext(NotificationContext)

  const {mutateAsync: setAppointmentStatus, isPending} =
    useSetAppointmentStatus({
      facilityId,
      appointmentId,
    })

  const handleConfirm = async () => {
    try {
      if (
        window.confirm(
          t(
            'Are you sure you want to mark this appointment as Did Not Attended?',
          ),
        )
      ) {
        await setAppointmentStatus({
          status: AppointmentStatus.NOSHOW,
          statusNote: note || undefined,
        })

        addNotification(
          t('Appointment successfully marked as Did Not Attended'),
          NotificationType.Success,
        )

        onClose()
      }
    } catch (error) {
      addNotification(
        `${t('Error marking appointment as DNA')}\n${
          error instanceof Error ? error.message : t('Unknown error occurred')
        }`,
        NotificationType.Error,
      )
    }
  }

  return (
    <SideModal
      isVisible={isVisible}
      onClose={onClose}
      header={
        <Container>
          <Typography size="lg" weight="medium">
            {t('Mark as Did Not Attended')}
          </Typography>
        </Container>
      }>
      <Container flex direction="column" spacing={2}>
        {showWarning && (
          <InfoCard
            title={t('Appointment already completed')}
            content={t(
              'This appointment has details saved. You can mark it as Did Not Attended, but the details will remain in the system.',
            )}
          />
        )}
        <Typography weight="medium" size="lg">
          {t('Appointment Information')}
        </Typography>
        <Container flex direction="column" spacing={1}>
          <Typography weight="medium" size="md">
            {t('Name')}
          </Typography>
          <Typography color="secondary" size="sm">
            {patientName}
          </Typography>

          {patientDob && (
            <>
              <Typography weight="medium" size="md">
                {t('Date of Birth')}
              </Typography>
              <Typography color="secondary" size="sm">
                {new Date(patientDob).toLocaleDateString()}
              </Typography>
            </>
          )}

          <Typography weight="medium" size="md">
            {t('Appointment date')}
          </Typography>
          <Typography color="secondary" size="sm">
            {new Date(originalDate).toLocaleString()}
          </Typography>
        </Container>

        <div style={{height: '1px'}} />

        <Container flex direction="column" spacing={1}>
          <Typography weight="medium" size="md">
            {t('Note (optional)')}
          </Typography>
          <TextArea
            value={note || ''}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('Add a note (optional)')}
            height="200px"
          />
        </Container>

        <Container flex justify="flex-end" spacing={2}>
          <Button onClick={handleConfirm} disabled={isPending}>
            {t('Mark as DNA')}
          </Button>
        </Container>
      </Container>
    </SideModal>
  )
}
